import React, { useState, useEffect } from "react";
import Slider from "./Slider";
import Board from "./Board";

const MemoizedSlider = React.memo(Slider);

function getIndexFromCoords(x_str, y_str, tot_rows_str, tot_cols_str) {
    const x = parseInt(x_str);
    const y = parseInt(y_str);
    const tot_rows = parseInt(tot_rows_str);
    const tot_cols = parseInt(tot_cols_str);
    if (x < 0 || x >= tot_cols || y < 0 || y >= tot_rows) return null;
    return y * tot_cols + x;
}

function getCoordsFromIndex(index_str, tot_rows_str, tot_cols_str) {
    const index = parseInt(index_str);
    const tot_rows = parseInt(tot_rows_str);
    const tot_cols = parseInt(tot_cols_str);

    if (index < 0 || index >= tot_rows * tot_cols) return null;
    const x = index % tot_cols;
    const y = Math.floor(index / tot_cols);

    // console.log(index, tot_rows, tot_cols, x, y, typeof x, typeof y);
    return [x, y];
}

function isWinningMove(
    squares,
    winningCombo,
    index_last_move,
    tot_rows_str,
    tot_cols_str
) {
    const index = parseInt(index_last_move);
    const rows = parseInt(tot_rows_str);
    const cols = parseInt(tot_cols_str);

    const player = squares[index];
    let [player_x, player_y] = getCoordsFromIndex(index, rows, cols);

    function checkDir(dirx, diry, plx, ply) {
        let placed = 1;
        let indices = [index]; // start with 1 because the last move itself is already placed
        let winDir = {
            x: dirx,
            y: diry,
        };
        let currCheckDir = {
            x: plx,
            y: ply,
        };

        let shouldCheckFront = true;
        let shouldCheckBack = true;

        // check Forward
        while (shouldCheckFront) {
            let iFront = getIndexFromCoords(
                currCheckDir.x + winDir.x,
                currCheckDir.y + winDir.y,
                rows,
                cols
            );
            if (iFront !== null && squares[iFront] === player) {
                placed += 1;
                indices.push(iFront);
                currCheckDir.x += winDir.x;
                currCheckDir.y += winDir.y;
            } else {
                shouldCheckFront = false;
            }
        }

        currCheckDir = {
            x: plx,
            y: ply,
        };

        // check Backward
        while (shouldCheckBack) {
            let iFront = getIndexFromCoords(
                currCheckDir.x - winDir.x,
                currCheckDir.y - winDir.y,
                rows,
                cols
            );
            if (iFront !== null && squares[iFront] === player) {
                placed += 1;
                indices.unshift(iFront);
                currCheckDir.x -= winDir.x;
                currCheckDir.y -= winDir.y;
            } else {
                shouldCheckBack = false;
            }
        }

        if (placed >= winningCombo) {
            return indices;
        }
        return false;
    }

    let dirs = [
        [0, 1],
        [1, 1],
        [1, 0],
        [1, -1],
    ];
    for (let dir of dirs) {
        let result = checkDir(dir[0], dir[1], player_x, player_y);
        if (result) {
            return result;
        }
    }
    return false;
}

function Game() {
    const p1 = "X";
    const p2 = "O";
    const [rows, setRows] = useState(10);
    const [cols, setCols] = useState(10);
    const [winningCombo, setWinningCombo] = useState(4);

    const startingPlayer = p1;
    const [nextPlayer, setNextPlayer] = useState(
        startingPlayer == p1 ? p1 : p2
    );
    const [squares, setSquares] = useState(Array(rows * cols).fill(null));
    const [gameStarted, setGameStarted] = useState(false);
    const [hasWinner, setHasWinner] = useState(false);
    const [winningIndices, setWinningIndices] = useState([]);

    useEffect(() => {
        setSquares(Array(rows * cols).fill(null));
    }, [rows, cols]);

    const handleClick = (i) => {
        const nextSquares = squares.slice();
        if (nextSquares[i] || hasWinner) return;
        if (nextPlayer == p1) {
            nextSquares[i] = p1;
            setNextPlayer(p2);
        } else {
            nextSquares[i] = p2;
            setNextPlayer(p1);
        }
        setHasWinner(isWinningMove(nextSquares, winningCombo, i, rows, cols));
        if (hasWinner) {
            // console.log(`${nextPlayer} has won the game`);
            setHasWinner(true);
            setSquares(nextSquares);
            return;
        }
        setSquares(nextSquares);
        setGameStarted(true);
    };

    const resetGame = () => {
        setSquares(Array(rows * cols).fill(null));
        setNextPlayer(startingPlayer);
        setHasWinner(false);
        setGameStarted(false);
        setWinningIndices([]);
    };

    return (
        <div className="interface ">
            <div className="content-wrapper">
                <div className="absolute-wrapper">
                    {gameStarted ? (
                        <div
                            className={`game-started-div ${
                                gameStarted ? `fade-in` : `fade-out`
                            }`}
                        >
                            <p className="game-started-message">
                                The game has started
                            </p>
                            <button
                                className="reset-button"
                                onClick={resetGame}
                            >
                                Reset the game
                            </button>
                            {hasWinner ? (
                                <h1 className="game-started-message">
                                    {nextPlayer == p1 ? p2 : p1} has won!
                                </h1>
                            ) : (
                                <div className="game-info">
                                    <h1>
                                        {nextPlayer == p1 ? p1 : p2}&apos;s turn
                                    </h1>
                                    <h2>
                                        {winningCombo} in a row needed to win!
                                    </h2>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            className={`sliders ${
                                gameStarted ? `fade-out` : ``
                            }`}
                        >
                            <MemoizedSlider
                                name="Winning combo"
                                min={Math.min(2, rows, cols)}
                                max={Math.max(rows, cols)}
                                value={winningCombo}
                                onChange={(e) =>
                                    setWinningCombo(parseInt(e.target.value))
                                }
                            />
                            <MemoizedSlider
                                name="Width"
                                min={winningCombo}
                                max={10}
                                value={cols}
                                onChange={(e) =>
                                    setCols(parseInt(e.target.value))
                                }
                            />
                            <MemoizedSlider
                                name="Height"
                                min={winningCombo}
                                max={10}
                                value={rows}
                                onChange={(e) =>
                                    setRows(parseInt(e.target.value))
                                }
                            />
                        </div>
                    )}
                </div>
                <Board
                    p1={p1}
                    specified_rows={rows}
                    specified_cols={cols}
                    onSquareClick={handleClick}
                    squares={squares}
                    winningIndices={winningIndices}
                />
            </div>
        </div>
    );
}

export default Game;
